import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

// Same popup as the CRM: each build writes /version.json (see craco.config.js);
// when the live one differs from the build this tab is running, ask to refresh.
const CHECK_INTERVAL_MS = 3 * 60 * 1000;
const BUILT_ID = process.env.REACT_APP_BUILD_ID || "dev";

export default function UpdateAvailableBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // "Later" hides it until the next check finds the update again.
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // local dev server has no build ID to compare against
    if (BUILT_ID === "dev") return;

    async function checkVersion() {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        const data = await response.json();
        if (data.id && data.id !== BUILT_ID) {
          setUpdateAvailable(true);
          setDismissed(false);
        }
      } catch {
        // a failed check just means we try again on the next interval
      }
    }

    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);
    function handleVisibility() {
      if (document.visibilityState === "visible") checkVersion();
    }
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  if (!updateAvailable || dismissed) return null;

  return (
    <>
      <style>{`
        @keyframes update-pop-in {
          0% { opacity: 0; transform: translateY(40px) scale(0.8); }
          70% { opacity: 1; transform: translateY(-6px) scale(1.03); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes update-dance {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          20% { transform: translateY(-10px) rotate(-14deg); }
          40% { transform: translateY(0) rotate(10deg) scaleX(-1); }
          60% { transform: translateY(-8px) rotate(-8deg) scaleX(-1); }
          80% { transform: translateY(0) rotate(12deg); }
        }
        @keyframes update-sparkle {
          0%, 100% { opacity: 0; transform: scale(0.4) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.1) rotate(180deg); }
        }
        @keyframes update-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.55); }
          50% { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
        }
        .update-pop { animation: update-pop-in 520ms cubic-bezier(.2,.9,.3,1.3) both; }
        .update-dancer { display: inline-block; animation: update-dance 1.1s ease-in-out infinite; }
        .update-spark { position: absolute; animation: update-sparkle 1.4s ease-in-out infinite; pointer-events: none; }
        .update-refresh { animation: update-glow 1.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .update-pop, .update-dancer, .update-spark, .update-refresh { animation: none !important; }
        }
      `}</style>

      <div
        className="update-pop"
        role="alert"
        style={{
          position: "fixed",
          bottom: "20px",
          right: "16px",
          width: "min(340px, calc(100vw - 32px))",
          zIndex: 100000,
          padding: "18px 18px 16px",
          borderRadius: "20px",
          background: "linear-gradient(135deg, #f97316, #ea580c)",
          color: "#FFFFFF",
          boxShadow: "0 18px 45px rgba(0,0,0,0.3)",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <span className="update-spark" style={{ top: "12px", left: "22px", fontSize: "16px" }}>✨</span>
        <span className="update-spark" style={{ top: "26px", right: "26px", fontSize: "14px", animationDelay: "0.5s" }}>⭐</span>
        <span className="update-spark" style={{ top: "62px", left: "46px", fontSize: "12px", animationDelay: "0.9s" }}>🎉</span>
        <span className="update-spark" style={{ top: "58px", right: "52px", fontSize: "13px", animationDelay: "0.3s" }}>✨</span>

        <div className="update-dancer" style={{ fontSize: "46px", lineHeight: 1, marginBottom: "8px" }} aria-hidden="true">
          🕺
        </div>
        <div style={{ fontSize: "17px", fontWeight: 900, marginBottom: "4px" }}>New update is here!</div>
        <div style={{ fontSize: "12.5px", opacity: 0.9, marginBottom: "14px" }}>
          Refresh now to get the latest Adiyash Auto.
        </div>

        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
          <button
            type="button"
            className="update-refresh"
            onClick={() => window.location.reload()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "9px 18px",
              border: "none",
              borderRadius: "999px",
              background: "#FFFFFF",
              color: "#ea580c",
              fontSize: "13px",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            <RefreshCw size={14} />
            Refresh now
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            style={{
              padding: "9px 14px",
              border: "1px solid rgba(255,255,255,0.6)",
              borderRadius: "999px",
              background: "transparent",
              color: "#FFFFFF",
              fontSize: "12px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Later
          </button>
        </div>
      </div>
    </>
  );
}
